import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { paletteCopy } from '@/copy'
import {
  type CommandPaletteGroup,
  CommandPalette as Palette,
} from '@/ui/CommandPalette/CommandPalette'
import { hasOpenLayer } from '@/ui/overlay/escapeStack'
import { ROUTES } from '../../constants/routes'
import {
  useWorkspaceAgentsQuery,
  useWorkspacesQuery,
} from '../../hooks/queries'
import { useTopicsQuery } from '../../hooks/useTopicsQuery'
import { useWorkspaceMembersQuery } from '../../hooks/useWorkspaceMembers'
import { useWorkspaceStore } from '../../store/workspace'

type Section = 'topics' | 'agents' | 'people' | 'actions'

interface PaletteItem {
  id: string
  section: Section
  label: string
  target: string
}

/**
 * ⌘K command palette (Phase 6 keyboard map): quick-jump over the active
 * workspace's topics, agents, and people. Owns the shortcut, the data and the
 * navigation; the look lives in the ui component.
 */
export function CommandPalette() {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const navigate = useNavigate()
  const wsId = useWorkspaceStore((s) => s.activeWorkspaceId) ?? ''
  const { data: workspaces = [] } = useWorkspacesQuery()
  const role = workspaces.find((w) => w.workspace_id === wsId)?.role
  const canManage = role === 'owner' || role === 'admin'

  const topics = useTopicsQuery(wsId, { enabled: open })
  const agents = useWorkspaceAgentsQuery(wsId, { enabled: open })
  const members = useWorkspaceMembersQuery(wsId, { enabled: open && !!wsId })

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        // The palette does not open over another overlay; it still toggles
        // itself closed.
        if (!open && hasOpenLayer()) return
        e.preventDefault()
        if (!open) setQuery('')
        setOpen(!open)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  const { groups, targets } = useMemo(() => {
    const q = query.trim().toLowerCase()
    const all: PaletteItem[] = [
      ...(topics.data ?? []).map((t) => ({
        id: `t-${t.topic_id}`,
        section: 'topics' as const,
        label: t.name,
        target: ROUTES.topic(t.topic_id),
      })),
      ...(agents.data ?? []).map((a) => ({
        id: `a-${a.agent_id}`,
        section: 'agents' as const,
        label: a.name,
        target: ROUTES.agentProfile(wsId, a.agent_id),
      })),
      ...(members.data ?? []).map((m) => ({
        id: `p-${m.user_id}`,
        section: 'people' as const,
        label: m.username,
        target: ROUTES.personProfile(wsId, m.username),
      })),
      ...(canManage && wsId
        ? [
            {
              id: 'action-new-agent',
              section: 'actions' as const,
              label: paletteCopy.actions.newAgent,
              target: ROUTES.agentNew(wsId),
            },
            {
              id: 'action-invite-member',
              section: 'actions' as const,
              label: paletteCopy.actions.inviteMember,
              target: ROUTES.workspaceSettingsTab(wsId, 'members'),
            },
          ]
        : []),
    ]
    const matched = q
      ? all.filter((i) => i.label.toLowerCase().includes(q))
      : all
    // D98: commands before content when the query matches both.
    const order: Section[] = q
      ? ['actions', 'topics', 'agents', 'people']
      : ['topics', 'agents', 'people', 'actions']
    const groups: CommandPaletteGroup[] = order
      .map((section) => ({
        id: section,
        heading: paletteCopy.groups[section],
        items: matched.filter((i) => i.section === section),
      }))
      .filter((g) => g.items.length > 0)
    return {
      groups,
      targets: new Map(matched.map((i) => [i.id, i.target])),
    }
  }, [topics.data, agents.data, members.data, query, wsId, canManage])

  const pending = topics.isLoading || agents.isLoading || members.isLoading
  const failed = [topics, agents, members].filter((r) => r.isError && !r.data)
  const error = failed.length ? paletteCopy.loadFailed : undefined

  return (
    <Palette
      open={open}
      onOpenChange={setOpen}
      label={paletteCopy.label}
      inputLabel={paletteCopy.input}
      placeholder={paletteCopy.input}
      value={query}
      onValue={setQuery}
      groups={groups}
      onSelect={(item) => {
        setOpen(false)
        const to = targets.get(item.id)
        if (to) void navigate(to)
      }}
      emptyMessage={paletteCopy.noMatches(query.trim())}
      pending={pending}
      error={error}
      onRetry={() => {
        for (const r of failed) void r.refetch()
      }}
    />
  )
}
